import React from 'react'
import styled from '@emotion/styled'
import { Mutation } from '@apollo/client/react/components'
import { Formik } from 'formik'
import { useViewer } from '../ViewerContext'
import * as yup from 'yup'
import {
  color,
  Alert,
  DialogBody,
  DialogFooter,
  Input,
  Button,
  space,
  StarRating,
} from '@ticketswap/solar'
import { GIVE_USER_FEEDBACK } from './giveUserFeedback'
import { useIntercom } from '~/components/IntercomProvider'

const InputWrapper = styled.div`
  display: grid;
  grid-gap: ${space[16]};
`

const StyledAlert = styled(Alert)`
  margin-top: ${space[8]};
`

const ChatToggle = styled.button`
  color: ${color.earth};
  outline: 0;
`

function Feedback({ t, ...props }) {
  const [rating, setRating] = React.useState(0)
  const [error, setError] = React.useState(null)
  const { viewer } = useViewer()
  const { start } = useIntercom()

  function getSentiment() {
    return rating === 0 ? null : rating < 3 ? 'NEGATIVE' : 'POSITIVE'
  }

  function isPositive() {
    return getSentiment() === 'POSITIVE'
  }

  return (
    <Mutation mutation={GIVE_USER_FEEDBACK}>
      {(send, { loading }) => (
        <Formik
          initialValues={{
            email: viewer ? viewer.email : '',
            name: viewer ? `${viewer.firstname} ${viewer.lastname}` : '',
            message: '',
          }}
          validationSchema={yup.object().shape({
            name: yup.string().required(!viewer),
            email: yup.string().email().required(!viewer),
            message: yup.string().required('Required'),
          })}
          onSubmit={async values => {
            setError(null)
            try {
              const {
                data: { giveUserFeedback: response },
              } = await send({
                variables: {
                  input: {
                    ...values,
                    rating: rating === 0 ? null : rating,
                    sentiment: getSentiment(),
                    url: global.location.href,
                  },
                },
              })
              if (response.errors.length > 0) {
                setError(response.errors[0].message)
              } else {
                props.setActiveView(isPositive() ? 'positive-confirm' : 'negative-confirm')
                sessionStorage.setItem(
                  isPositive() ? 'positive_feedback_submitted' : 'negative_feedback_submitted',
                  true
                )
              }
            } catch (error) {
              console.log(error)
            }
          }}
        >
          {({ values, isValid, dirty, handleChange, handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <DialogBody>
                <InputWrapper>
                  {error && <Alert variant="error">{error}</Alert>}

                  {viewer && (
                    <StarRating
                      label={t('common:feedbackDialog.fields.rating.label')}
                      onChange={setRating}
                    />
                  )}

                  {rating !== 0 && !isPositive() && (
                    <StyledAlert>
                      {t('common:feedbackDialog.alert.info')}{' '}
                      <ChatToggle
                        type="button"
                        onClick={() => {
                          props.hide()
                          start()
                        }}
                      >
                        {t('common:feedbackDialog.alert.button')}
                      </ChatToggle>{' '}
                      {t('common:feedbackDialog.alert.call')}
                    </StyledAlert>
                  )}

                  {!viewer && (
                    <>
                      <Input
                        type="text"
                        name="name"
                        id="name"
                        label={t('common:feedbackDialog.fields.name.label')}
                        value={values.name}
                        onChange={handleChange}
                      />
                      <Input
                        type="email"
                        name="email"
                        id="email"
                        label={t('common:feedbackDialog.fields.email.label')}
                        value={values.email}
                        onChange={handleChange}
                      />
                    </>
                  )}

                  <Input
                    as="textarea"
                    rows="6"
                    name="message"
                    id="message"
                    label={t('common:feedbackDialog.fields.message.label')}
                    value={values.message}
                    onChange={handleChange}
                    help={viewer && t('common:feedbackDialog.fields.message.help')}
                  />
                </InputWrapper>
              </DialogBody>
              <DialogFooter>
                <Button
                  type="submit"
                  width="full"
                  loading={loading}
                  disabled={
                    values.message.length < 3 ||
                    (viewer && rating === 0) ||
                    !isValid ||
                    !dirty ||
                    loading
                  }
                >
                  {t('common:feedbackDialog.button')}
                </Button>
              </DialogFooter>
            </form>
          )}
        </Formik>
      )}
    </Mutation>
  )
}

export default Feedback
